// File: src/contexts/SearchContext.tsx
// Simple React context for global search with debounced live search

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface SearchContextType {
  searchQuery: string;
  debouncedSearchQuery: string;
  setSearchQuery: (query: string) => void;
  currentContext: string;
  setCurrentContext: (context: string) => void;
}

export const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentContext, setCurrentContext] = useState('');

  // Debounce the search query for live search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay for debouncing

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <SearchContext.Provider value={{
      searchQuery,
      debouncedSearchQuery,
      setSearchQuery,
      currentContext,
      setCurrentContext
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
